import { useContext, type ChangeEvent } from 'react'

import {
  Button,
  Form,
  ModalContext,
  SectionDivider,
  SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useManageInvoice from '@hooks/useManageInvoice'
import useInvoice from '@hooks/useInvoice'

import EntitySelector from '@components/EntitySelector'

const MODAL_KEY = 'CreateAdminInvoiceModal'

type ModalCallbacks = {
  closeModal: () => void,
  createInvoice: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateAdminInvoiceModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createInvoice } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const invoice = modalPayload.invoice || null

  const invoicePayload = useInvoice(invoice)
  const {
    callbacks: { createInvoice: createFn },
    creating,
  } = invoicePayload

  const adminInvoicePayload = useManageInvoice({ invoice })
  const {
    callbacks: {
      getEntityString,
      handleEntitySelectorChange,
      handleEntityStateChange,
      handleOrderChange,
    },
    filteredOrders,
    invoiceFormPayload,
    isAdmin,
    isProviderDirector,
    provider,
  } = adminInvoicePayload

  const {
    entityState,
    entityState: {
      address, debtor_id, debtor_type, order_id, order_title, reference,
    },
    handlers,
    saveEnabled,
  } = invoiceFormPayload

  const createInvoicePayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: {
      ...entityState,
    },
    shouldRedirect: true,
    toastText: 'Invoice Created',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={MODAL_KEY}
      showModal={showModal}
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title="Create"
        titleSecondLine="Invoice"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Debtor Type">
            <select
              onChange={(e: ChangeEvent<HTMLSelectElement>) => handleEntityStateChange(e)}
              value={debtor_type}
            >
              <option value="">Please Select...</option>
              <option value="Agency">Agency</option>
              {!isProviderDirector && <option value="Provider">Provider</option>}
              {isProviderDirector && <option value="Provider">{provider?.title}</option>}
            </select>
          </Form.Field>

          {(isAdmin || debtor_type === 'Agency') && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2719
                selectItem: entity => handleEntitySelectorChange({ entity }),
              }}
              entityKey={getEntityString(debtor_type)}
              label="Debtor Name"
              marginTop="large"
              selectedItemId={debtor_id}
              searchRequestOptions={{ billable: true }}
            />
          )}
        </Form>

        <Form.Field label="* Order" marginTop="large">
          <select
            name="order_id"
            value={order_id}
            onChange={e => handleOrderChange(e)}
          >
            <option value="">No Order</option>
            {debtor_type === 'Agency' && filteredOrders.map(order => (
              <option key={order.id} value={order.id}>
                {order.title}
              </option>
            ))}
          </select>
        </Form.Field>

        <Form.Field label="* Reference/Campaign" marginTop="large">
          <input name="reference" type="text" value={reference} {...handlers} />
        </Form.Field>

        <Form.Field label="* Order Title" marginTop="large">
          <input
            name="order_title"
            type="text"
            value={order_title}
            {...handlers}
          />
        </Form.Field>

        <SectionDivider marginTop="large">DEBTOR ADDRESS</SectionDivider>

        <Form.Field label="Street Address" marginTop="large">
          {/*
           // @ts-expect-error TS2322 */}
          <textarea name="address" style={{ height: 80, resize: 'vertical' }} value={address} {...handlers} />
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => createInvoice(createInvoicePayload)}
          size="large"
        >
          {saveEnabled ? 'Create Invoice' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateAdminInvoiceModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
